import { ChangeDetectionStrategy, Component, input, InputSignal, output, OutputEmitterRef, resource, ResourceRef } from '@angular/core';
import { MatList, MatListItem, MatListItemIcon, MatListItemLine, MatListItemMeta, MatListItemTitle } from '@angular/material/list';
import { IconButtonComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { FileInputValueModel, FileValidationErrorModel } from '@libs/form/models';
import { IconName } from '@libs/shared/models';
import { FileFormatPipe } from './file-format.pipe';
import { FilePreviewModel } from './file-preview.model';

@Component({
  selector: 'ui-file-preview-list',
  standalone: true,
  imports: [
    MatList,
    MatListItem,
    MatListItemIcon,
    MatListItemLine,
    MatListItemMeta,
    MatListItemTitle,
    IconButtonComponent,
    IconComponent,
    FileFormatPipe,
  ],
  templateUrl: './file-preview-list.component.html',
  styleUrls: ['./file-preview-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePreviewListComponent {
  private readonly imageIcons: Record<string, IconName> = {
    'image/jpeg': 'file-jpg',
    'image/png': 'file-png',
    'image/gif': 'file-gif',
  };

  private readonly applicationIcons: Record<string, IconName> = {
    'application/pdf': 'file-pdf',
    'application/msword': 'file-doc',
    'application/vnd.ms-excel': 'file-xls',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
  };

  public readonly values: InputSignal<FileInputValueModel[]> = input.required<FileInputValueModel[]>();
  public readonly errors: InputSignal<FileValidationErrorModel[] | undefined> = input();
  public readonly editable: InputSignal<boolean> = input(true);
  public readonly singleColumn: InputSignal<boolean> = input(false);

  public readonly remove: OutputEmitterRef<number> = output();

  public readonly previews: ResourceRef<FilePreviewModel[]> = resource({
    params: () => ({ values: this.values(), errors: this.errors() }),
    loader: ({ params }) => this.buildPreviews(params.values, params.errors),
    defaultValue: [],
  });

  private async buildPreviews(values: FileInputValueModel[], errors?: FileValidationErrorModel[]): Promise<FilePreviewModel[]> {
    const previews: FilePreviewModel[] = await Promise.all(
      values.map((file, index) => this.createPreviewItem(file.file, index, errors)),
    );

    return [
      ...previews.filter(preview => !preview.error),
      ...previews.filter(preview => !!preview.error),
    ];
  }

  private async createPreviewItem(file: File, index: number, errors?: FileValidationErrorModel[]): Promise<FilePreviewModel> {
    const result: FilePreviewModel = {
      index,
      name: file.name,
      icon: this.isImage(file) ? this.getImageIconName(file) : this.getAppIconName(file),
      error: errors?.find(error => error.data === file),
    };

    if (!result.error && this.isImage(file)) {
      result.src = await this.readFile(file);
    }

    return result;
  }

  private readFile(file: File): Promise<string> {
    return new Promise((resolve) => {
      const reader: FileReader = new FileReader();

      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      reader.onload = (): void => void resolve(String(reader.result));

      reader.onerror = (): void => {

        console.error(`Error reading file: ${file.name}`);

        resolve('');
      };

      reader.readAsDataURL(file);
    });
  }

  private isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  private getImageIconName(file: File): IconName {
    return this.imageIcons[file.type] ?? 'file-image';
  }

  private getAppIconName(file: File): IconName {
    return this.applicationIcons[file.type] ?? 'file';
  }
}
